import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CheckCircle2, XCircle, PartyPopper } from "lucide-react";
import { cn } from "@/lib/utils";
import { motion, AnimatePresence } from "framer-motion";

interface FillBlankProps {
  question: string;
  correctAnswer: string;
  hint?: string;
  acceptableAnswers?: string[];
  onAnswer: (answer: string, isCorrect: boolean) => void;
  disabled?: boolean;
}

const FillBlankActivity = ({ question, correctAnswer, hint, acceptableAnswers, onAnswer, disabled }: FillBlankProps) => {
  const [answer, setAnswer] = useState("");
  const [answered, setAnswered] = useState(false);
  const [isCorrect, setIsCorrect] = useState(false);

  const handleSubmit = () => {
    if (!answer.trim() || answered) return;
    const trimmed = answer.trim().toLowerCase();
    const allAcceptable = [correctAnswer, ...(acceptableAnswers || [])].map(a => a.toLowerCase().trim());
    const correct = allAcceptable.includes(trimmed);
    setIsCorrect(correct);
    setAnswered(true);
    onAnswer(answer, correct);
  };

  return (
    <div className="space-y-4 max-w-lg mx-auto">
      <h3 className="text-lg font-bold text-foreground text-center">{question}</h3>

      <div className="flex items-center gap-3">
        <Input
          value={answer}
          onChange={(e) => setAnswer(e.target.value)}
          placeholder={hint || "اكتب إجابتك هنا..."}
          disabled={answered || disabled}
          className={cn(
            "text-center text-lg font-medium",
            answered && isCorrect && "border-green-500 bg-green-50",
            answered && !isCorrect && "border-red-500 bg-red-50"
          )}
          onKeyDown={(e) => e.key === "Enter" && handleSubmit()}
        />
        {answered && isCorrect && <CheckCircle2 className="w-6 h-6 text-green-600 shrink-0" />}
        {answered && !isCorrect && <XCircle className="w-6 h-6 text-red-500 shrink-0" />}
      </div>

      {!answered && (
        <div className="flex justify-center">
          <Button onClick={handleSubmit} disabled={!answer.trim()} className="px-8">
            تأكيد الإجابة
          </Button>
        </div>
      )}

      <AnimatePresence>
        {answered && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className={cn(
              "text-center p-4 rounded-xl",
              isCorrect ? "bg-green-50 dark:bg-green-950/30" : "bg-red-50 dark:bg-red-950/30"
            )}
          >
            {isCorrect ? (
              <div className="flex items-center justify-center gap-2 text-green-700 dark:text-green-400">
                <PartyPopper className="w-5 h-5" />
                <span className="font-bold">أحسنت! إجابة صحيحة 🌟</span>
              </div>
            ) : (
              <div className="text-red-600 dark:text-red-400">
                <span className="font-bold">الإجابة الصحيحة: </span>
                <span className="text-sm">{correctAnswer}</span>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default FillBlankActivity;
